import { useState } from "react";
import classNames from "classnames";
import { Button } from "@/components/shared/inputs/button/button";
import { Slider } from "@/components/shared/data-display/slider/slider";
import { Link } from "@/components/shared/inputs/link/link";
import { logError } from "@/infrastructure/observability/error-logging.service";
import {
  setWorkAuthorFollow,
  setWorkSeriesFollow,
} from "@/services/work.service";
import type { SliderRecommendationItem } from "@/types/slider.types";
import {
  WorkAuthorSeriesWidgetType,
  type WorkAuthorSeriesWidget,
} from "@/types/work.types";
import { loginPageCallbackLink } from "@/utility/url";
import styles from "./work-author-slider.module.scss";

interface WorkAuthorSliderProps {
  widget: WorkAuthorSeriesWidget | null;
  isLoggedIn: boolean;
  hasBackground?: boolean;
}

interface WorkFollowButtonProps {
  initialIsFollowing: boolean;
  label: string;
  subject: "Series" | "Author";
  onChange: (wantsFollow: boolean) => Promise<boolean>;
}

/**
 * Renders a follow control for the work's author or series and keeps its
 * optimistic display state local to the slider.
 * @param root0 Component properties.
 * @param root0.initialIsFollowing Whether the entity is initially followed.
 * @param root0.label Entity name used by the accessible button label.
 * @param root0.subject Entity type displayed in the button text.
 * @param root0.onChange Persists a requested follow-state change.
 * @returns The author or series follow control.
 */
const WorkFollowButton = ({
  initialIsFollowing,
  label,
  subject,
  onChange,
}: WorkFollowButtonProps) => {
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFollow = async () => {
    setIsLoading(true);
    try {
      setIsFollowing(await onChange(!isFollowing));
    } catch (error) {
      logError(error, { context: `WorkAuthorSlider:follow${label}` });
    } finally {
      setIsLoading(false);
    }
  };

  let action = "+ Follow";
  if (isFollowing) action = isHovering ? "Unfollow" : "✓ Following";

  return (
    <div
      className={styles.followButtonShell}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Button
        text={`${action} ${subject}`}
        size="small"
        loading={isLoading}
        disabled={isLoading}
        onClick={handleFollow}
        ariaLabel={isFollowing ? `Unfollow ${label}` : `Follow ${label}`}
        rounded
        className={styles.followButton}
      />
    </div>
  );
};

/**
 * Renders the Legacy-compatible author or series recommendation slider,
 * including responsive headings and follow controls.
 * @param root0 Component properties.
 * @param root0.widget Author or series recommendation data to render.
 * @param root0.isLoggedIn Whether follow actions can be sent immediately.
 * @param root0.hasBackground Whether to use the highlighted panel treatment.
 * @returns The recommendation slider, or nothing when it has no items.
 */
const WorkAuthorSlider = ({
  widget,
  isLoggedIn,
  hasBackground = false,
}: WorkAuthorSliderProps) => {
  if (!widget?.items.length) return null;

  const author = widget.author;
  const series =
    widget.type === WorkAuthorSeriesWidgetType.Series ? widget.series : null;

  const redirectToLogin = () => {
    if (!isLoggedIn) {
      window.location.assign(loginPageCallbackLink(window.location.href));
      return true;
    }
    return false;
  };

  const handleAuthorFollow = async (wantsFollow: boolean) => {
    if (redirectToLogin() || !author) return false;
    return setWorkAuthorFollow(author.idAuthor, wantsFollow);
  };

  const handleSeriesFollow = async (wantsFollow: boolean) => {
    if (redirectToLogin() || !series) return false;
    return setWorkSeriesFollow(series.idSeries, wantsFollow);
  };

  let seriesName: string | null = null;
  if (series) {
    seriesName = series.name.toLowerCase().endsWith(" series")
      ? series.name
      : `${series.name} Series`;
  }

  return (
    <section
      className={classNames(styles.root, {
        [styles.hasBackground]: hasBackground,
      })}
      aria-label={
        seriesName && author
          ? `${seriesName} by ${author.name}`
          : (seriesName ?? `More by ${author?.name ?? "author"}`)
      }
    >
      <div
        className={classNames(styles.headingGroup, {
          [styles.combinedHeading]: series && author,
        })}
      >
        {series && seriesName && author && (
          <h2 className={classNames("u-title-sm", styles.narrowHeading)}>
            <Link href={series.url} linkType="legacy">
              {seriesName}
            </Link>{" "}
            <span className={styles.titleDivider} aria-hidden="true">
              |
            </span>
            {" by "}
            <Link href={author.url} linkType="legacy">
              {author.name}
            </Link>
          </h2>
        )}
        {series && seriesName && (
          <div className={styles.headingRow}>
            <h2 className={classNames("u-title-sm", styles.wideHeading)}>
              <Link href={series.url} linkType="legacy">
                {seriesName}
              </Link>
            </h2>
            <WorkFollowButton
              initialIsFollowing={series.isFollowing}
              label={series.name}
              subject="Series"
              onChange={handleSeriesFollow}
            />
          </div>
        )}
        {author && (
          <div className={styles.headingRow}>
            <h2 className={classNames("u-title-sm", styles.wideHeading)}>
              {series ? "by " : "More by "}
              <Link href={author.url} linkType="legacy">
                {author.name}
              </Link>
            </h2>
            <WorkFollowButton
              initialIsFollowing={author.isFollowing}
              label={author.name}
              subject="Author"
              onChange={handleAuthorFollow}
            />
          </div>
        )}
      </div>
      {widget.description && (
        <p className={styles.description}>{widget.description}</p>
      )}
      <Slider
        variant="legacy"
        title={null}
        description={null}
        flag={null}
        url={null}
        guidUserRecommendation={null}
        items={widget.items.map(
          (item): SliderRecommendationItem => ({
            idIQ: null,
            idWork: item.idWork,
            idAmazon: null,
            title: item.title,
            authors: item.authors || null,
            synopsis: null,
            media: null,
            idMedia: null,
            quality: null,
            idQuality: null,
            imageUrl: item.imageUrl,
            url: item.url,
            price: item.price,
            idDefaultListItem: null,
          }),
        )}
      />
    </section>
  );
};

export default WorkAuthorSlider;
